import { Check, ListOrdered, Plus } from "lucide-react";
import { useState } from "react";
import { DisciplineRatingLevelsDialog } from "@/components/admin/DisciplineRatingLevelsDialog";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type Discipline,
  useCreateDiscipline,
  useDisciplines,
  useUpdateDiscipline,
} from "@/hooks/use-disciplines";
import { DEFAULT_DISCIPLINE_COLOR, DISCIPLINE_PALETTE } from "@/lib/discipline-colors";
import { cn } from "@/lib/utils";

function PalettePicker({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {DISCIPLINE_PALETTE.map((c) => {
        const selected = c.hex === value;
        return (
          <button
            key={c.hex}
            type="button"
            onClick={() => onChange(c.hex)}
            title={c.name}
            aria-label={c.name}
            aria-pressed={selected}
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-full ring-offset-2 ring-offset-background transition",
              selected ? "ring-2 ring-foreground" : "hover:ring-2 hover:ring-foreground/30",
            )}
            style={{ backgroundColor: c.hex }}
          >
            {selected && <Check className="h-3.5 w-3.5 text-white" />}
          </button>
        );
      })}
    </div>
  );
}

export function DisciplineLibrary() {
  const {
    data: disciplines = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useDisciplines({ includeInactive: true });
  const [createOpen, setCreateOpen] = useState(false);

  if (isError) {
    return <ErrorState message={error?.message ?? "Failed to load"} onRetry={() => refetch()} />;
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Discipline Library</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Structural categories assigned to Resources. Used by the Pod Composition Template and
            Assignment workflows. Discipline does <strong>not</strong> affect Fit Score.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Discipline
        </Button>
      </header>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : disciplines.length === 0 ? (
        <p className="text-muted-foreground text-sm">No Disciplines defined yet.</p>
      ) : (
        <div className="rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Color</th>
                <th className="px-4 py-2 font-medium">Code</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {disciplines.map((d) => (
                <DisciplineRow key={d.id} discipline={d} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateDisciplineDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function DisciplineRow({ discipline }: { discipline: Discipline }) {
  const [editOpen, setEditOpen] = useState(false);
  const [levelsOpen, setLevelsOpen] = useState(false);
  const update = useUpdateDiscipline();
  return (
    <>
      <tr className="border-t">
        <td className="px-4 py-2">
          <span
            className="inline-block h-4 w-4 rounded-full border border-border align-middle"
            style={{ backgroundColor: discipline.color }}
            title={discipline.color}
          />
        </td>
        <td className="px-4 py-2 font-mono">{discipline.code}</td>
        <td className="px-4 py-2">{discipline.name}</td>
        <td className="px-4 py-2">
          <Badge variant={discipline.active ? "default" : "secondary"}>
            {discipline.active ? "Active" : "Deprecated"}
          </Badge>
        </td>
        <td className="px-4 py-2 text-right">
          <Button variant="ghost" size="sm" onClick={() => setLevelsOpen(true)}>
            <ListOrdered className="mr-1.5 h-3.5 w-3.5" /> Rating levels
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => update.mutate({ id: discipline.id, active: !discipline.active })}
            disabled={update.isPending}
          >
            {discipline.active ? "Deactivate" : "Reactivate"}
          </Button>
        </td>
      </tr>
      <EditDisciplineDialog open={editOpen} onOpenChange={setEditOpen} discipline={discipline} />
      <DisciplineRatingLevelsDialog
        discipline={discipline}
        open={levelsOpen}
        onOpenChange={setLevelsOpen}
      />
    </>
  );
}

function CreateDisciplineDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>(DEFAULT_DISCIPLINE_COLOR);
  const create = useCreateDiscipline();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Discipline</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(
              { code, name, color },
              {
                onSuccess: () => {
                  setCode("");
                  setName("");
                  setColor(DEFAULT_DISCIPLINE_COLOR);
                  onOpenChange(false);
                },
              },
            );
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="discipline-code">Code (2 or 3 characters, A–Z or 0–9)</Label>
            <Input
              id="discipline-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={3}
              minLength={2}
              pattern="[A-Z0-9]{2,3}"
              placeholder="e.g. SA1"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="discipline-name">Name</Label>
            <Input
              id="discipline-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Solution Architect"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Color</Label>
            <PalettePicker value={color} onChange={setColor} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDisciplineDialog({
  open,
  onOpenChange,
  discipline,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  discipline: Discipline;
}) {
  const [name, setName] = useState(discipline.name);
  const [color, setColor] = useState(discipline.color);
  const update = useUpdateDiscipline();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {discipline.code}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate(
              { id: discipline.id, name, color },
              { onSuccess: () => onOpenChange(false) },
            );
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="discipline-rename">Name</Label>
            <Input
              id="discipline-rename"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Color</Label>
            <PalettePicker value={color} onChange={setColor} />
          </div>
          <p className="text-xs text-muted-foreground">
            Code is immutable; rename does not break existing Resource or template references.
          </p>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
